import {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    type ReactNode,
} from 'react';
import Bowser from 'bowser';

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptContextType {
    canInstall: boolean;
    isIOS: boolean;
    isStandalone: boolean;
    promptInstall: () => void;
    dismiss: () => void;
}

const InstallPromptContext = createContext<InstallPromptContextType | null>(null);

const STORAGE_KEY = 'install-prompt-dismissed';
const DISMISS_DAYS = 30;

function isDismissed(): boolean {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const dismissedAt = Number(stored);
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
}

function isStandaloneMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
        || ('standalone' in navigator && (navigator as Record<string, unknown>).standalone === true);
}

function detectIOSSafari(): boolean {
    const browser = Bowser.getParser(navigator.userAgent);
    return browser.getOSName(true) === 'ios' && browser.getBrowserName(true) === 'safari';
}

export function InstallPromptProvider({ children }: { children: ReactNode }) {
    const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
    const [canInstall, setCanInstall] = useState(false);
    const [isStandalone] = useState(isStandaloneMode);
    const [isIOS] = useState(detectIOSSafari);

    useEffect(() => {
        if (isStandalone || isDismissed()) return;

        // On iOS there's no beforeinstallprompt — show banner based on platform detection.
        if (isIOS) {
            setCanInstall(true);
            return;
        }

        const handler = (e: Event) => {
            e.preventDefault();
            deferredPrompt.current = e as BeforeInstallPromptEvent;
            setCanInstall(true);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [isStandalone, isIOS]);

    const promptInstall = () => {
        if (deferredPrompt.current) {
            deferredPrompt.current.prompt();
            deferredPrompt.current.userChoice.then(({ outcome }) => {
                if (outcome === 'accepted') {
                    setCanInstall(false);
                }
                deferredPrompt.current = null;
            });
        }
    };

    const dismiss = () => {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        setCanInstall(false);
    };

    return (
        <InstallPromptContext.Provider value={{ canInstall, isIOS, isStandalone, promptInstall, dismiss }}>
            {children}
        </InstallPromptContext.Provider>
    );
}

export function useInstallPrompt(): InstallPromptContextType {
    const context = useContext(InstallPromptContext);
    if (!context) {
        throw new Error('useInstallPrompt must be used within an InstallPromptProvider');
    }
    return context;
}
